import { endOfWeek, startOfWeek } from "date-fns";
import moment from "moment";
export function generateDays(startDate, endDate) {
  const days = [];
  let currentDate = new Date(startDate);
  while (currentDate <= endDate) {
    days.push(new Date(currentDate));
    currentDate.setDate(currentDate.getDate() + 1);
  }
  return days.map(formatDate);
}

export const getComparator = (date) => {
  return moment(date).format("MM-DD-YYYY");
};
export const getWeekIdentifierString = ({ firstDay, lastDay }) => {
  return `${moment(firstDay).format("MMM,DD")} - ${moment(lastDay).format(
    "MMM,DD"
  )}`;
};
export const getYearFormatter = (date) => {
  return moment(date).format("YYYY-MM-DD");
};
export function formatDate(date) {
  const currentDate = new Date();
  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const weekends = ["Sat", "Sun"];
  const day = daysOfWeek[date.getDay()];
  const month = date.getMonth() + 1;
  const dayOfMonth = date.getDate();
  return {
    dayLabel: day,
    dateLabel: `${month}/${dayOfMonth}`,
    fullDate: date,
    comparator: getComparator(date),
    isFutureDate: date > currentDate,
    isWeekend: weekends.includes(day),
  };
}
export const getWeekRange = (date) => {
  return {
    firstDay: startOfWeek(new Date(date), { weekStartsOn: 1 }),
    lastDay: endOfWeek(new Date(date), { weekStartsOn: 1 }),
  };
};
export const getWeekRangeFromDate = (date) => {
  const parsed = moment.isMoment(date) ? date : moment(date, ["MM-DD-YYYY", moment.ISO_8601], true);
  return {
    firstDay: parsed.clone().startOf("isoWeek").toDate(),
    lastDay: parsed.clone().endOf("isoWeek").toDate(),
  };
};
export const getCurrentWeek = () => {
  return {
    firstDay: startOfWeek(new Date(), { weekStartsOn: 1 }),
    lastDay: endOfWeek(new Date(), { weekStartsOn: 1 }),
  };
};

export const getQueryParams = (obj) => {
  const truthyObj = Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value)
  );
  return new URLSearchParams(truthyObj).toString();
};
export const unWindData = (tmData) => {
  const newData = tmData.map((cur) => {
    const formattedData = cur.hours.map((hr) => {
      return {
        taskId: cur.projectTaskId,
        payCode: cur.payCode,
        taskName: cur.projectTaskName,
        taskType: cur.billableType,
        hours: hr.hours,
        detailId: cur.detailId,
        payloadHours: hr.hours === null ? 0 : hr.hours,
        isInCompleteRow: hr.hours !== null && !cur.projectTaskId,
        key: `${cur.projectTaskId}-${cur.billableType}-${hr.comparator}-${cur.payCode}`,
        date: getYearFormatter(hr.comparator),
      };
    });
    return formattedData;
  });
  const flattenedData = newData.flat();
  const groupedData = flattenedData.reduce((acc, cur) => {
    const exRowIndex = acc.findIndex((row) => {
      return row.key === cur.key;
    });
    if (exRowIndex > -1) {
      acc[exRowIndex].hours += cur.hours || 0;
      acc[exRowIndex].payloadHours += cur.payloadHours || 0;
    } else {
      acc.push(cur);
    }
    return acc;
  }, []);
  return groupedData;
};
export const generateWeekDaysInfo = (firstDay, lastDay, holidays) => {
  const generatedDays = generateDays(firstDay, lastDay);
  generatedDays.forEach((day) => {
    day.isHoliday = day.isWeekend || holidays.includes(day.comparator);
  });
  return generatedDays;
};
export const isValidDate = (dateStr) => {
  return moment(dateStr, moment.ISO_8601, true).isValid();
};
export const isCurrentYear = (date) => {
  return moment(date).year() === moment().year();
};
export const mapResponseDataToUi = (data) => {
  if (Array.isArray(data) && data.length) {
    const holidayDates = data
      .filter((date) => date.IsHoliday)
      .map((dt) => getComparator(dt.WorkDate));
    const timsheetWeekRange = getWeekRange(data[0].WorkDate);
    const weekDaysArr = generateWeekDaysInfo(
      timsheetWeekRange.firstDay,
      timsheetWeekRange.lastDay,
      holidayDates
    );
    const mappedData = data.map((dt) => {
      const { Details, WorkDate } = dt;
      const comparator = getComparator(WorkDate);
      const taskLevelDetails = (Array.isArray(Details) ? Details : []).map(
        (task) => {
          const {
            ProjectTaskID,
            ProjectTaskName,
            PayCode,
            TaskType,
            Hours,
            DetailID,
          } = task;
          return {
            projectTaskId: ProjectTaskID,
            projectTaskName: ProjectTaskName,
            projectTask: ProjectTaskID,
            payCode: PayCode,
            billableType: TaskType,
            detailId: DetailID,
            comparator: comparator,
            date: WorkDate,
            key: `${ProjectTaskID}-${TaskType}-${PayCode}`,
            hoursVal: Hours,
          };
        }
      );
      return taskLevelDetails;
    });
    const groupedData = mappedData.flat().reduce((acc, cur) => {
      const exRowIndex = acc.findIndex((rowVal) => rowVal.key === cur.key);
      if (exRowIndex > -1) {
        const hrsRefIndex = acc[exRowIndex].hours.findIndex(
          (hr) => hr.comparator === cur.comparator
        );
        if (hrsRefIndex > -1) {
          acc[exRowIndex].hours[hrsRefIndex].hours = cur.hoursVal;
        }
      } else {
        acc.push({
          ...cur,
          hours: weekDaysArr.map((week) => {
            const isSameDayRef = week.comparator === cur.comparator;
            if (isSameDayRef) {
              return {
                day: new Date(cur.date),
                hours: cur.hoursVal,
                comparator: cur.comparator,
              };
            }
            return {
              day: new Date(week.fullDate),
              hours: null,
              comparator: week.comparator,
            };
          }),
        });
      }
      return acc;
    }, []);
    return groupedData.sort(
      (a, b) =>
        b.hours.filter((hr) => hr.hours).length -
        a.hours.filter((hr) => hr.hours).length
    );
  } else {
    return [];
  }
};
export const replaceErrorText = (text) => {
  if (typeof text === "string" && text) {
    return text.replace("Error:", "");
  }
  return text;
};
export const formatTimeEntriesForReports = (data) => {
  if (!data || data.length === 0) return [];

  const transformed = {};

  data.forEach((entry) => {
    const {
      ProjectTaskID,
      TaskType,
      PayCode,
      Hours,
      Date: dateRef,
      ProjectTaskName,
    } = entry;
    const timeKey = `${ProjectTaskID}-${TaskType}-${PayCode}`;
    const comparator = dateRef;
    if (!transformed[timeKey]) {
      transformed[timeKey] = {
        projectTaskId: ProjectTaskID,
        projectTaskName: ProjectTaskName,
        projectTask: ProjectTaskID,
        payCode: PayCode,
        billableType: TaskType,
        detailId: Math.floor(Math.random() * 1000),
        comparator,
        date: new Date(dateRef).toISOString(),
        key: timeKey,
        hoursVal: Hours,
        hours: [],
      };
    }

    transformed[timeKey].hours.push({
      day: new Date(dateRef).toISOString(),
      hours: Hours,
      comparator,
    });
  });

  return Object.values(transformed);
};
export const getFileGroupingDetailsByUrl = (string) => {
  try {
    let url = new URL(string);
    const { pathname } = url;
    const fileName = pathname.replace("/", "");
    const fileDetails = {
      accessUrl: string,
      orignalFile: string,
      fileName,
      extension: fileName.split(".").pop(),
    };
    return fileDetails;
  } catch (_) {
    return "";
  }
};

export const GET_YEAR_FILTER_OPTIONS = () => {
  const currentYear = new Date().getFullYear();
  const THRESHOLD = 3;
  const options = [];
  for (let i = 0; i < THRESHOLD; i++) {
    const pastYear = currentYear - i;
    options.push({
      label: `${pastYear}`,
      value: pastYear,
    });
  }
  return options;
};
